import { useEffect, useState } from 'react';

/**
 * Observa as seções pelos ids informados e devolve o id da mais visível no
 * viewport — usado para destacar o link ativo do menu (scrollspy).
 *
 * Guardado contra ambiente sem `IntersectionObserver` (é o caso do jsdom nos
 * testes): sem ele, o hook simplesmente não atualiza e devolve o primeiro id,
 * em vez de quebrar a página.
 */
export function useScrollspy(ids: readonly string[]): string | null {
  const primeiroId = ids[0] ?? null;
  const [ativo, setAtivo] = useState<string | null>(primeiroId);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;

    const elementos = ids
      .map((id) => document.getElementById(id))
      .filter((elemento): elemento is HTMLElement => elemento !== null);
    if (elementos.length === 0) return undefined;

    const razaoPorId = new Map<string, number>();

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          razaoPorId.set(
            entrada.target.id,
            entrada.isIntersecting ? entrada.intersectionRatio : 0,
          );
        }

        let melhorId: string | null = null;
        let melhorRazao = 0;
        for (const [id, razao] of razaoPorId) {
          if (razao > melhorRazao) {
            melhorRazao = razao;
            melhorId = id;
          }
        }
        if (melhorId) setAtivo(melhorId);
      },
      // A faixa de gatilho fica no terço superior da tela: a seção "vira"
      // ativa quando cruza ali, não só quando ocupa a tela inteira.
      { rootMargin: '-20% 0px -70% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    for (const elemento of elementos) observador.observe(elemento);
    return () => observador.disconnect();
  }, [ids]);

  return ativo;
}
